import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, useParams, Redirect } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useQuery } from 'react-query'
import { encodeBase64Fake, getRemotedbUrl, getStageName, i18nIntl, imgPath, isNotEmpty, throwErrorEvent } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import ActionButton, { SecondaryButton, SquaredButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection.js';
import ConfirmButton from '../builder/ConfirmButton';
import FieldLabel from '../builder/FieldLabel.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import QtyPicker from '../builder/QtyPicker.js';
import Spinner from '../builder/Spinner';
import TextArea from '../builder/TextArea.js';

import { operators, useFilterData, usePaginator } from '../FilterContent';
import ModuleHeader from '../ui_items/ModuleHeader.js';

import StageSelector from './elements/StageSelector.js';
import NavigationTabs from '../builder/NavigationTabs.js';
import Select from '../builder/Select.js';
import Paginator from '../utils/Paginator.js';
import ActionLink from '../builder/ActionLink.js';
import { TicketUserData } from '../front/layers/UserParticipationsMultistageList.js';
import { useOwner } from '../context/context.js';
import SingleRegisteredEntry from './SingleRegisteredEntry.js';
import { SecurityUserAction } from '../promotion/SecurityCenter.js';
import Tooltip from '../builder/Tooltip.js';
import TabledItemsList, { Column } from '../builder/TabledItemsList.js';
import RibbonCollection from '../ui_items/RibbonCollection.js';
import SearchField from '../builder/SearchField.js';
import Message, { AlertMessage, InformationMessage } from '../builder/Message.js';
import { isStageOpen } from '../../data/bootstrap_stages.js';
import ToggleButton from '../builder/ToggleButton.js';
import { GetRuleSubtitle, GetRuleTitle } from './OcrTicketSidebar.js';
import { get_all_validators } from '../../lib/tag_validators.js';
import ContentSelector from '../Form/ContentSelector.js';
import JSONEdit from '../Form/JSONEdit.js';
import { reprocess_ticket, save_stage_ticket } from '../../lib/stage_ocr_ticket.js';
import Reprocessing from './TicketOcr/Reprocessing.js';
import OcrTagEnrichment from './TicketOcr/OCRTagEnrichment.js';
import { fields_by_type, getTagColorByType, getTagTitleByType } from '../account/TagValidators.js';
import RawDataViewer from '../builder/RawDataViewer.js';
import SaveButton from '../builder/SaveButton.js';
import AddArticle from '../AddArticle.js';
import Checkbox from '../builder/Checkbox.js';

const datetime_format = window?.datetime_format ?? 'L, HH:mm [h]';

const ColumnCustomContent = (props) => {
	const display = props?.display ?? true;
	if(display === false) return null;

	return (
		<Column {...props} />
	)
};

const ColumnActions = (props) => {
	return (
		<Column {...props} class="gui_text_align_end" />
	)
};

const TableOfContents = ({ items, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const columns = [
		<ColumnCustomContent
			main={true}
			title={i18n("Date")}
			type='date'
		>{props.created}</ColumnCustomContent>,
		<ColumnCustomContent
			title={i18n("Agent")}
		>{props.agent}</ColumnCustomContent>,
		<ColumnCustomContent
			title={i18n("User")}
		>{props.user}</ColumnCustomContent>,
		<ColumnCustomContent
			title={i18n("Action")}
		>{props.action}</ColumnCustomContent>,
		<ColumnActions
			type='actions'
		>{props.actions}</ColumnActions>,
	];

	const default_column_defs = '150px 1fr 1fr 120px 90px';

	return (
		<TabledItemsList
			table_id='alerts'
			items={items}
			columns={columns}
			empty_message={props.empty_message}
			max_height='calc(100dvh - 390px - 1em)'
			//sticky_actions={true}
			column_defs={{
				gridTemplateColumns:props?.column_defs ?? default_column_defs,
			}}
			_class={{ other:'not_in_top' }}
		/>
	)
};

const NavigationLinks = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = props?.stage?.Stage ?? {};
	
	let icon_activity = 'knk-buy-ticket';
	if(stage.reqs_type == '2') icon_activity = 'knk-promotional-code';

	const section = props?.section ?? '';
	const periods = [
		{ k:1, v:i18n("Today") },
		{ k:7, v:i18n("Last week") },
		{ k:30, v:i18n("Last month") },
		{ k:2000, v:i18n("Forever") },
	];
	const sections = [];
	if(props.go_back_section != undefined) sections.push(props.go_back_section);
	if(props.filterByStatus != undefined) {
		sections.push({
			label:`${i18n("In review")} [${props?.totals?.pending ?? 0}]`,
			icon:'knk-loading',
			//onClick:() => props.filterByStatus(2),
			full_link:`/${stage.id}/inreview`,
			autotranslate:false,
			selected:(section == 'inreview') ? true : false,
			router:true,
		});
		sections.push({
			label:`${i18n("Rejected")} [${props?.totals?.rejected ?? 0}]`,
			icon:'knk-blocked',
			//onClick:() => props.filterByStatus(3),
			full_link:`/${stage.id}/rejected`,
			autotranslate:false,
			selected:(section == 'rejected') ? true : false,
			router:true,
		});
	}
	if(!props.is_premoderator){
		sections.push({
			label:`${i18n("Activity")}`,
			icon:icon_activity,
			full_link:`/${stage.id}/activity`,
			autotranslate:false,
			selected:(section == 'activity') ? true : false,
			router:true,
		});
	}
	return (
		<nav class="_sticky_navigation">
			<NavigationTabs
				sections={sections}
				_class={{ other:'gui_pos_rel_important' }}
			>
				{(props.filterByPeriod != undefined) && (
					<ButtonCollection>

						{(section != 'activity') && (
							<Select
								name="period"
								change={(k, value) => props.filterByPeriod(value)}
								value={props.period}
								values={periods}
								_field_class="in_top"
								_class={{ first:true, size:70 }}
							>Created</Select>
						)}

						{(section == 'activity') && (
							<SearchField
								_class={{ first:true }}
								_field_class="in_top"
								name="search_n"
								value={props.search_user}
								change={(k, value) => props.searchUser(value)}
								placeholder="Search by user"
							/>
						)}

						<ActionButton
							disabled={true}
							autotranslate={false}
						>{getStageName(stage, i18n)}</ActionButton>
					</ButtonCollection>
				)}
			</NavigationTabs>
		</nav>
	)
};

const Wrapper = ({ children, ...props }) => {
	return (
		<section class="gui_users_registered">
			<NavigationLinks
				{...props}
			/>

			<article
				class="entries_list gui_pos_rel"
				style={{
					minHeight:'300px',
					paddingBlockStart:'2em',
				}}
			>
				{children}
			</article>
		</section>
	)
};

const FooterValidationActions = (props) => {
	const enable_auto_navigate = (props?.is_last_ticket ?? false) ? false : true;

	return (
		<ButtonCollection
			_class={{ other:'end_actions' }}
		>
			<SecondaryButton
				onClick={props.onGoBack}
			>Go back</SecondaryButton>

			<ConfirmButton
				onClick={props.onConfirm}
				disabled={props?.confirm_disabled ?? true}
				_position='top'
			>{(enable_auto_navigate) ? "Confirm and review the next receipt" : "Confirm and close"}</ConfirmButton>
		</ButtonCollection>
	)
};

const UserTicketComparison = ({ stage, ...props }) => {
	if(!props.tickets_to_compare) {
		return (
			<Spinner>Loading tickets to compare</Spinner>
		)
	}

	const additional_tickets = (props?.tickets_to_compare ?? []);
	const is_ocr_advanced = (stage.Stage.reqs_type == 8) ? true : false;

	return (
		<ul class="other_tickets">
			{additional_tickets.map((stage_ticket, t) => {
				const ticket = stage_ticket?.StageTicket ?? stage_ticket;
				const ticket_status = (ticket.status == 0) ? 1 : ticket.status;//els pendents es consideren acceptats
				return (
					<li key={`add_ticket_${t}`}>
						<ImageTicket
							is_ocr_advanced={is_ocr_advanced}
							image_url={ticket.data.graphic_info}
							img_basedir={props.img_basedir}
							status={ticket_status}
							created={ticket.created}
							display_pending={true}
							in_layer={true}
							intl={props.intl}
							promotion={props.promotion}
						/>
					</li>
				)
			})}
		</ul>
	)
};

const FoundArticles = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const condition_matches = props?.condition_matches ?? [];
	const matches = props?.matches ?? [];
	const type = props?.type ?? false;

	const display_found_articles = (condition_matches.length > 0 && matches.length > 0) ? true : false;

	if(display_found_articles === false) return null;

	let total_quantity = 0;
	let total_amount = 0;

	const avail_matches = [];
	condition_matches.map((match) => {
		const matched_tag = matches.find((m) => (m.id == match));
		if(matched_tag == undefined) return;
		avail_matches.push(matched_tag);

		total_quantity += matched_tag.quantity;
		total_amount += matched_tag.total;
	});

	const classes = ['user_feedback'];
	if(props?.in_bottom ?? false) classes.push('in_bottom');

	if(type == 'store') {
		classes.push('wrong_condition');

		return (
			<div class={classes.join(" ")}>
				<strong>{i18n("No required store was found in the receipt.")}</strong>
			</div>
		)
	}

	if(avail_matches.length == 0 && type == 'articles') {
		classes.push('wrong_condition');

		return (
			<div class={classes.join(" ")}>
				<strong>{i18n("No required articles were found in the receipt.")}</strong>
			</div>
		)
	}

	classes.push('found_condition');

	return (
		<div class={classes.join(" ")}>
			<strong class="t">{i18n("Found articles")}:</strong>

			<strong class="t qty">{i18n("Total quantity")}:</strong>

			<strong class="t tot">{i18n("Total amount")}:</strong>

			{avail_matches.map((match, m) => {
				const found_tags = [];
				const value_matches = match?.value_matches ?? [];

				(match?.values ?? []).map((tag, t) => {
					const tag_value = (tag ?? '').trim();	// whole string of item
					let tag_found = value_matches?.[t] ?? '';	// substring found in tag_value
					if(`${tag_value}`.toLowerCase() == `${tag_found}`.toLowerCase()) {
						found_tags.push({
							title:tag_value,
						});
						return;
					}

					const tag_regex = new RegExp(tag_found, 'gi');

					found_tags.push({
						title:tag_value.replace(tag_regex, `<span>${tag_found}</span>`),
						with_highlight:true,
					});
				})

				let classes = '';
				if(m < 1) classes = ' first';

				return (
					<React.Fragment key={`found_${match.id}`}>
						<RibbonCollection
							items={found_tags}
							_class={{ other:classes }}
						/>
						
						<span class={`qty${classes}`}>{match.quantity}</span>
						
						<span class={`tot${classes}`}>{match.total}</span>
					</React.Fragment>
				)
			})}

			<span class="f ff sum">{i18n("Total")}:</span>

			<span class="f qty sum">{total_quantity}</span>

			<span class="f tot sum">{total_amount}</span>
		</div>
	)
};

const PremoderationRuleStatus = ({ type, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const status = props?.status ?? false;

	const [ is_opened, setIsOpened ] = useState((status === false) ? true : false);

	useEffect(() => {
		setIsOpened((props.status === false) ? true : false);
	}, [ props.status ]);

	const user_feedback = props?.user_feedback?.feedback?.feedback ?? "";

	const icon_classes = ['i'];
	icon_classes.push((status) ? 'ep-check-list-item greenIcon' : 'ep-delete-circle redIcon');

	const edit_fields = {
		ticket_total:{
			include_fields:['footer.total']
		},
		ticket_dates:{
			include_fields:['header.date','header.time']
		},
		article: {
			include_fields:['line_items.*.article','line_items.*.sku', 'line_items.*.quantity', 'line_items.*.total'],
		},
		store:{
			include_fields:fields_by_type.store.map((i) => (i.k)),
		},
		location:{
			include_fields:fields_by_type.location.map((i) => (i.k)),
		},
	};

	const expected_condition = GetRuleSubtitle(type, props.condition, props.validation_tags, props.intl, 'conditions');
	
	return (
		<article class="premoderation_rule_status" style={{ order:props?.order ?? 1 }}>
			<div
				class="rule_header"
				onClick={(status === false) ? () => setIsOpened(!is_opened) : null}
			>
				<span class={icon_classes.join(" ")} />

				<strong class="t">{i18n(GetRuleTitle(type))}</strong>

				<RibbonCollection
					items={[{
						title:GetRuleSubtitle(type, props.condition, props.validation_tags, props.intl, 'match'),
						color:'black',
					}]}
				/>

				{(status === false) && (
					<ToggleButton
						squared={true}
						active={is_opened}
						color='darkgray'
						icon='knk-arrow-t-big'
						icon_off='knk-arrow-b-big'
						_class={{ other:'not_bordered_button toggle_visibility' }}
					/>
				)}
			</div>

			{(is_opened) && (
				<div class="rule_body">
					
					<FoundArticles
						condition_matches={props.condition.match}
						matches={props.matches}
						type={props.condition.type}
						in_bottom={(expected_condition != "" || user_feedback !== "") ? false : true}
						intl={props.intl}
					/>

					{(expected_condition != "") && (
						<div class={`user_feedback wrong_condition${(user_feedback !== "") ? '' : ' in_bottom'}`}>
							<strong>{i18n("Condition")}</strong>: {expected_condition}
						</div>
					)}

					{(user_feedback !== "") && (
						<div class="user_feedback in_bottom">
							<strong>{i18n("User feedack")}</strong>: {user_feedback}
						</div>
					)}

					<ContentSelector>
						<ButtonCollection>
							{(props.condition?.match) && (
								<ContentSelector.Selector id="refine">
									<SecondaryButton
										_class={{ other:'not_in_top' }}
									>Refine tag</SecondaryButton>
								</ContentSelector.Selector>
							)}

							<ContentSelector.Selector id="edit">
								<SecondaryButton
									_class={{ other:'not_in_top' }}
								>Edit receipt</SecondaryButton>
							</ContentSelector.Selector>
							
							<ContentSelector.Selector id="add">
								<SecondaryButton
									_class={{ other:'not_in_top' }}
								>Add article</SecondaryButton>
							</ContentSelector.Selector>
						</ButtonCollection>

						<ContentSelector.Content id="refine">
							<OcrTagEnrichment
								condition={props.condition}
								validation_tags={props.validation_tags}
							/>

							<ButtonCollection style={{ marginBlockStart:'0.9em' }}>
								<ContentSelector.CloseButton>
									<SecondaryButton>Finish refining</SecondaryButton>
								</ContentSelector.CloseButton>
							</ButtonCollection>
						</ContentSelector.Content>

						<ContentSelector.Content id="edit">
							{/* <ButtonCollection
								_class={{ other:'gui_sticky_actions' }}
							>
								<ContentSelector.CloseButton>
									<SecondaryButton>Finish editing</SecondaryButton>
								</ContentSelector.CloseButton>
							</ButtonCollection> */}

							<JSONEdit
								json={props.output_ai}
								onSave={(json) => {
									const new_ocr_ticket_props = { ...props.stage_ocr_ticket, output_ai:json };
									props.setStageOcrTicket(new_ocr_ticket_props);
									save_stage_ticket(new_ocr_ticket_props, props.promotion_id)
								}}
								{...edit_fields[type]}
							/>

							{/* <ButtonCollection>
								<ContentSelector.CloseButton>
									<SecondaryButton
										_class={{ in_top:false }}
										style={{ marginBlockStart:'1em' }}
									>Finish editing</SecondaryButton>
								</ContentSelector.CloseButton>
							</ButtonCollection> */}
						</ContentSelector.Content>


						<ContentSelector.Content id="add">
							
							<AddArticle
								json={props.output_ai}
								onSave={(json) => {
									const new_ocr_ticket_props = { ...props.stage_ocr_ticket, output_ai:json };
									props.setStageOcrTicket(new_ocr_ticket_props);
									save_stage_ticket(new_ocr_ticket_props, props.promotion_id)
								}}
							/>
						</ContentSelector.Content>
					</ContentSelector>
				</div>
			)}
		</article>
	)
};

const MatchedTags = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const matches = props?.matches ?? [];
	const validation_tags = props?.validation_tags ?? [];

	if(matches.length == 0) {
		return (
			<div class="matches_found">
				{i18n("No matches found.")}

				{props.children}
			</div>
		)
	}

	const tags = [];
	matches.map((match, m) => {
		const tag = validation_tags.find((t) => (parseInt(t.OcrTag.id) == match.id));
		if(tag == undefined) return;

		const tag_matches = `${i18n("Matched")}:<br />- ${(match?.value_matches ?? []).join("<br />- ")}`;

		const t = {
			ribbon:{
				title:`${getTagTitleByType(tag.OcrTag)}: ${match.name}`,
				color:getTagColorByType(tag.OcrTag),
				tooltip:<Tooltip
					position='top'
					//open={(m < 1) ? true : null}
				>{tag_matches}</Tooltip>,
			},
			//matches:match?.value_matches ?? [],
		};

		if(tag.OcrTag.type == 'article') {
			t.quantity = match.quantity;
			t.total = match.total;
		}

		tags.push(t);
	});

	if(tags.length == 0) {
		return (
			<div class="matches_found">
				{i18n("No matches found.")}

				{props.children}
			</div>
		)
	}

	return (
		<div class="matches_found">
			{tags.map((tag, t) => {
				return (
					<div key={`ftag_${t}`}>
						<RibbonCollection
							items={[tag.ribbon]}
						/>

						{isNotEmpty(tag, 'quantity') && (
							<span>({i18n("Quantity")}: {tag.quantity})</span>
						)}

						{isNotEmpty(tag, 'total') && (
							<span>({i18n("Amount")}: {tag.total})</span>
						)}
					</div>
				)
			})}

			{props.children}
		</div>
	)
};

const OcrDataPremoderation = ({ stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const ocr_data = props?.ocr_data ?? false;
	if(ocr_data === false) return null;

	const [ stage_ocr_ticket, setStageOcrTicket ] = useState(ocr_data);
	const [ is_processing, setIsProcessing ] = useState(false);
	const [ handwritten_detected ] = useState(ocr_data.output_ai?.controls?.handwritten == 1);

	const matches = ocr_data?.matches ?? [];
	const output_ai = ocr_data?.output_ai ?? {};
	const metadata = ocr_data?.metadata ?? {};
	const user_feedback = ocr_data?.user_feedback ?? [];
	const failed_conditions = metadata?.conditions ?? [];
	const store_name = ocr_data?.store_name ?? false;
	const display_validation = props?.display_validation ?? true;

	useEffect(() => {
		setIsProcessing(props.is_processing);
	}, [ props.is_processing ]);

	useEffect(() => {
		setStageOcrTicket(props.ocr_data);
	}, [ props.ocr_data ]);

	let ticket_date = `${output_ai?.header?.date} ${output_ai?.header?.time ?? '00:00:00'}`;
	if(ticket_date.indexOf(" PM") > -1) ticket_date.replace(" PM", ":00");
	if(ticket_date.indexOf(" AM") > -1) ticket_date.replace(" AM", ":00");
	
	const editingSoftware = () => {
		const exif = output_ai?.exif ?? {};
		if(exif.Software == undefined) return false;
		
		let has_edited = false;
		if(exif.Software.indexOf("Photoshop") > -1) has_edited = "Adobe Photoshop";

		return has_edited;
	};

	const has_edited = editingSoftware();


	if(is_processing) {
		return (
			<div>
				<Spinner>Reprocessing data</Spinner>
			</div>
		)
	}

	return (
		<>
			<dl class="gui_ocr_advanced_data">
				{(has_edited !== false) && (
					<div>
						<dt class="knk-alert redIcon">{i18n("EXIF")}:</dt>

						<dd>
							<FormattedMessage
								id="This may have been processed with {software}."
								values={{
									software:<strong>{has_edited}</strong>,
								}}
							/>
						</dd>
					</div>
				)}

				{(store_name !== false) && (
					<div>
						<dt class="knk-get-users-to-pos">{i18n("Store name")}:</dt>

						<dd>{stage_ocr_ticket.output_ai?.header?.store_name}</dd>
					</div>
				)}

				<div>
					<dt class="knk-money">{i18n("Receipt amount")}:</dt>

					<dd>{stage_ocr_ticket.output_ai?.footer?.total}</dd>
				</div>

				{(ticket_date !== false) && (
					<div>
						<dt class="knk-calendar">{i18n("Receipt date")}:</dt>

						<dd>{EPDateFormat(ticket_date, datetime_format)}</dd>
					</div>
				)}

				<div>
					<dt class="knk-tag">{i18n("Tags found")}:</dt>

					<dd>
						<MatchedTags
							matches={stage_ocr_ticket.matches}
							validation_tags={props.validation_tags}
							intl={props.intl}
						/>
					</dd>
				</div>
			</dl>

			{(display_validation) && (
				<div class="rule_status_list">

					{(handwritten_detected) && ( 
						<Message 
							icon={false}
							_class={{ other:'in_top in_bottom' }}
						>
							<p>{i18n("Handwriting detected in the receipt.")}</p>

							<Checkbox 
								name="handwritten" 
								change={(k, v) => {
									const new_ocr_ticket_props = { ...stage_ocr_ticket };
									new_ocr_ticket_props.output_ai.controls.handwritten = (v === false) ? 1 : 0;
									setStageOcrTicket(new_ocr_ticket_props);
									save_stage_ticket(new_ocr_ticket_props, stage.promotion_id)
								}}
								value={!stage_ocr_ticket.output_ai?.controls?.handwritten}
								_class={{ first:true }}
							>Accept handwriting</Checkbox>
						</Message>
					)}

					{(stage?.reqs_ocrticket_conditions ?? []).map((condition, r) => {
						const has_failed = failed_conditions.find((fc) => (fc.id == condition.id));
						const status = (has_failed != undefined) ? false : true;

						return (
							<PremoderationRuleStatus
								key={`rule_${r}`}
								output_ai={output_ai}
								stage_ocr_ticket={stage_ocr_ticket}
								setStageOcrTicket={setStageOcrTicket}
								type={condition.type}
								condition={condition}
								user_feedback={(status === false) ? user_feedback.find((uf) => (uf.condition_id == condition.id)) : null}
								matches={matches}
								status={status}
								promotion_id={stage.promotion_id}
								validation_tags={props.validation_tags}
								order={(status === false) ? r : (100 + r)}
								intl={props.intl}
							/>
						)
					})}
				</div>
			)}
		</>
	)
};

const UserFullInfo = ({ openWholeUserLayer, close, ...props }) => {
	const data = props?.data ?? false;
	if(data === false) return null;

	const user = data?.user_data ?? {};

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const user_name = user?.name ?? false;
	const login_id = user?.login_id ?? null;
	
	const promotion = props.promotion;
	const tz = promotion.timezone;

	return (
		<>
			<div class="extra_user_info">
				{(isNotEmpty(data, 'created')) && (
					<div class="gui_not_in_bottom_small">
						<span>{i18n("Participation date")}: <strong>{EPGetTimezone(data.created, tz, datetime_format)}</strong></span>
					</div>
				)}

				{(user_name !== false) && (
					<div class="gui_not_in_bottom_small">
						<span>{i18n("***Name")}:&nbsp;
						{(login_id && openWholeUserLayer != undefined) ? (
							<ActionLink
								onClick={() => openWholeUserLayer(login_id)}
								_class={{other:'gui_cutTextOnOverflow gui_force_bold gui_not_in_start_micro'}}
								autotranslate={false}
							>{user_name}</ActionLink>
						) : (
							<strong>{user_name}</strong>
						)}
						</span>
					</div>
				)}
	
				<TicketUserData
					data={data.data}
					fields={props.fields}
					promotion_id={props.promotion_id}
					display_ticket={false}
					intl={props.intl}
				/>
			</div>
		</>
	)
};

const TicketRawDataVisualization = ({ ...props }) => {
	const output_ai_raw = { ...props.data?.ocr_data?.output_ai ?? {} };

	/*if(Array.isArray(output_ai_raw.exif) && output_ai_raw.exif.length < 1) {
		delete output_ai_raw.exif;
	}*/
	delete output_ai_raw.exif;	// we don't use the exif data

	return (
		<>
			<div class="c">
				<div style={{ '--_height':'var(--layer-max-h)' }}>
					<RawDataViewer
						raw_data={output_ai_raw}
						is_standalone={true}
						scroll_enabled={false}
					/>
				</div>
			</div>

			<ButtonCollection
				_class={{ other:'end_actions gui_flex_end' }}
			>
				<SecondaryButton
					onClick={props.backButton}
				>Go back</SecondaryButton>
			</ButtonCollection>
		</>
	)
};

const TicketValidation = ({ stage, openWholeUserLayer, close, test_mode, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const max_selection_units = 20;

	const [ status, setStatus ] = useState(props?.status ?? null);
	const [ is_other, setIsOther ] = useState(false);
	const [ reason, setReason ] = useState(false);
	const [ participations, setParticipations ] = useState(1);
	const [ max_units, setMaxUnits ] = useState(max_selection_units);

	const [ is_processing, setIsProcessing ] = useState(false);

	const reqs_multiparticipation = stage?.Stage?.reqs_premoderation_multiparticipation ?? false;

	useEffect(() => {
		if(props.status != undefined) setStatus(props.status);
	}, [ props.status ]);

	const accept = () => {
		if(props.onAccept != undefined) props.onAccept(participations);
	};

	const reject = () => {
		if(props.onReject != undefined) props.onReject(reason);
	};

	const resetPreviousSelection = () => {
		setStatus(null);
		setReason(false);
		setIsOther(false);
	};

	const goBack = () => {
		resetPreviousSelection();
		if(props.onStatusChange != undefined) props.onStatusChange(null);
	};

	useEffect(() => {
		let num_tickets = 1;
		const _ticket = props?.data?.data ?? [];

		if(reqs_multiparticipation && _ticket.ticket_amount != undefined && stage.Stage.reqs_ocr_amount_x_stamp != undefined) {
			const amount = parseFloat(stage.Stage.reqs_ocr_amount_x_stamp);
			num_tickets = Math.floor(_ticket.ticket_amount / amount);
			setParticipations(num_tickets);
		}

		setMaxUnits(Math.max(num_tickets, max_selection_units));
	}, []);

	if(status == 'ko') {
		const has_other = stage?.Stage?.reqs_premoderation_reason_other ?? false;
		const has_reasons = (stage.Stage?.reqs_premoderation_reasons ?? []).length > 0;

		const confirm_disabled = (reason === false && (has_reasons || has_other)) ? true : false;

		return (
			<>
				<div class="c">
					<div class="scroller">
						<FieldLabel>Identify the reason for discarding that best fits</FieldLabel>

						<div class="reason_picker">
							{has_reasons && stage.Stage.reqs_premoderation_reasons.map((r) => {
								if(!isNotEmpty(r, 'title')) return null;

								return (
									<ActionButton
										key={`reason_${r.id}`}
										onClick={() => {
											setReason(r);
											setIsOther(false);
										}}
										_class={{ other:`wrap_text${(reason.id == r.id) ? ' selected' : ''}` }}
										autotranslate={false}
									>{r.title}</ActionButton>
								)
							})}

							{(has_other) && (
								<ActionButton
									onClick={() => {
										setIsOther(true);
										setReason(false);
									}}
									_class={{ other:`wrap_text${(is_other) ? ' selected' : ''}` }}
								>Other</ActionButton>
							)}
						</div>

						{(is_other) && (
							<TextArea
								name='reason'
								change={(k, v) => setReason({ id:'other', title:v })}
								after_message="This will be visible to the participant."
							>Specify a reason</TextArea>
						)}
					</div>
				</div>

				<FooterValidationActions
					status={props.status}
					onConfirm={reject}
					onGoBack={goBack}
					confirm_disabled={confirm_disabled}
					is_last_ticket={props.is_last_ticket}
				/>
			</>
		)
	}

	if(status == 'ok') {
		//const punch_card_stage = (stage.Stage.type == stage_categories.ID_STAMPSCARD_CATEGORY.id) ? true : false;
		//const direct_stage = (stage.Stage.type == stage_categories.ID_FORM_CATEGORY.id) ? true : false;

		return (
			<>
				<div class="c">
					<div class="main_section">
						{/*(punch_card_stage || (direct_stage && !stage.Stage.reqs_premoderation_manual_participation)) && (*/}
						{(reqs_multiparticipation) && (
							<>
								<FieldLabel>How many entries do you want to give?</FieldLabel>

								<QtyPicker
									max_units={max_units}
									units={participations}
									onUpdateUnits={(total) => setParticipations(total)}
								/>

								{(isNotEmpty(stage.Stage, 'reqs_ocr_amount_x_stamp')) && (
									<div class="gui_not_in_bottom_small gui_not_in_top_small">
										<span>{i18n("Amount per stamp")}:
										<strong class="gui_not_in_start_tiny">{stage.Stage.reqs_ocr_amount_x_stamp}</strong>
										<Tooltip position='top'>Amount added corresponding to each stamp on the card. For example, if your currency is the Euro and you specify 10, it means that for every 10 Euros spent according to the total purchase receipt, a new stamp will be issued on the card.</Tooltip>
										</span>
									</div>
								)}
								
								<div class="gui_not_in_top_small">
									<UserFullInfo
										data={props.data}
										fields={stage.Stage.reqs_form}
										promotion_id={stage.Stage.promotion_id}
										openWholeUserLayer={openWholeUserLayer}
										close={close}
										intl={props.intl}
										promotion={props.promotion}
									/>
								</div>
							</>
						)}
					</div>
				</div>

				<FooterValidationActions
					status={props.status}
					onConfirm={accept}
					onGoBack={goBack}
					confirm_disabled={false}
					is_last_ticket={props.is_last_ticket}
				/>
			</>
		)
	}
	
	const ticket_status = parseInt(props?.data?.status ?? 2)
	const ticket = props?.data ?? {};
	const ticket_detail = ticket?.data ?? [];

	const is_ocr_advanced = (stage.Stage.reqs_type == 8) ? true : false;
	let date_accepted,date_rejected,date_error,not_validated = null;
	if(!is_ocr_advanced){
		date_accepted = ([0, 1].includes(ticket_status)) && isNotEmpty(ticket_detail, 'accepted') ? ticket_detail.accepted : null;
		date_rejected = ([3].includes(ticket_status)) && isNotEmpty(ticket_detail, 'rejected') ? ticket_detail.rejected : null;
		not_validated = [1].includes(ticket_status) && !date_accepted && !date_rejected;//"Accepted" before enable premoderation tool
	}else{
		date_accepted = ([0, 1].includes(ticket_status)) && isNotEmpty(ticket_detail, 'ocr_ticket_id') ? ticket.created : null;
		date_rejected = ([3].includes(ticket_status)) && isNotEmpty(ticket_detail, 'ocr_ticket_id') ? ticket.created : null;
		date_error = ([9].includes(ticket_status)) && isNotEmpty(props?.data ?? {}, 'created') ? (props?.data?.created ?? null) : null;
		not_validated = [1].includes(ticket_status) && !date_accepted && !date_rejected && !date_error;
	}
	const date_validated = date_accepted ?? (date_rejected ?? date_error);
	
	const promotion = props.promotion;
	const tz = promotion.timezone;
	if(ticket_status == 9){
		const metadata = props.data.ocr_data?.metadata
		return (
			<div class="c">
				<UserFullInfo
					data={props.data}
					fields={stage.Stage.reqs_form}
					promotion_id={stage.Stage.promotion_id}
					openWholeUserLayer={openWholeUserLayer}
					close={close}
					intl={props.intl}
					promotion={props.promotion}
				/>
				<div class="redColor gui_force_xbold">{i18n("Rejected automatically by system")}</div>
				<div class="gui_not_in_bottom_small gui_not_in_top_tiny">
					<p>{i18n("Error")}: <strong>{metadata?.error ?? i18n("Unknown")}</strong></p>
					<p>{i18n("Error data")}: <strong>{(metadata?.errors ?? []).join(', ')}</strong></p>
				</div>
			</div>
		)
	}

	return (
		<>
			<div class="c">
				<UserFullInfo
					data={props.data}
					fields={stage.Stage.reqs_form}
					promotion_id={stage.Stage.promotion_id}
					openWholeUserLayer={openWholeUserLayer}
					close={close}
					intl={props.intl}
					promotion={props.promotion}
				/>
				

				<OcrDataPremoderation
					ocr_data={props.data.ocr_data}
					stage={stage.Stage}
					tz={tz}
					validation_tags={props.validation_tags}
					is_processing={is_processing}
					intl={props.intl}
				/>

				{(date_validated) && (
					<>
						<FieldLabel>{`${props.intl.formatMessage({ id:"Receipt {label_action} details" }, { label_action:date_accepted ? i18n("Acceptance", !window.is_spanish ? 'lowercase' : '') : i18n("Rejection", !window.is_spanish ? 'lowercase' : '')})}`}</FieldLabel>

						<div class="gui_not_in_bottom_small gui_not_in_top">
							<span>{i18n("Date")}: <strong>{EPGetTimezone(date_validated, tz, datetime_format)}</strong></span>
						</div>

						{(date_accepted && isNotEmpty(ticket_detail, 'participations') && parseInt(ticket_detail?.participations ?? 1) > 1) && (
							<div class="gui_not_in_bottom_small">
								<span>{i18n("Num. participations")}: <strong>{ticket_detail?.participations ?? 1}</strong></span>
							</div>
						)}
						
						{(isNotEmpty(ticket_detail, 'agent_id')) && (
							<div class="gui_not_in_bottom_small">
								<span>{i18n("Agente")}: <strong>
								{(ticket_detail.agent_id == props.agent_id) ? (
									`${i18n("You")}`
								) : (
									`${props?.data?.agent_name ?? ''} (#${ticket_detail.agent_id})`
								)}
								</strong></span>
							</div>
						)}
						
						{(isNotEmpty(ticket_detail, 'reason')) && (
							<div class="gui_not_in_bottom_small">
								<span>{i18n("Reason")}: <strong>{ticket_detail.reason?.title ?? i18n("Unknown")}</strong></span>
							</div>
						)}
					</>
				)}

				{(!date_validated) && (
					<>
						{(isNotEmpty(stage.Stage, 'reqs_premoderation_instructions')) && (
							<div class="main_section instructions">
								<FieldLabel>Instructions for determining receipt validity</FieldLabel>

								<div dangerouslySetInnerHTML={{ __html:stage.Stage.reqs_premoderation_instructions }} />
							</div>
						)}

						{(not_validated) && (
							<InformationMessage
								icon={false}
								_class={{ other:'in_bottom' }}
							><p>{i18n("This receipt was not reviewed from the premoderation tool.")}</p></InformationMessage>
						)}
					</>
				)}
			</div>

			{(!date_validated && !not_validated) && (
				<ButtonCollection
					_class={{ other:'end_actions' }}
				>
					<ActionButton
						onClick={() => {
							resetPreviousSelection();
							setStatus('ko');
							if(props.onStatusChange != undefined) props.onStatusChange('ko');
						}}
						color='red'
						icon='knk-off'
					>Reject</ActionButton>

					<ActionButton
						color='orange'
						icon='knk-help'
						onClick={close}
					>Decide later</ActionButton>

					{(is_ocr_advanced) && (
						<ActionButton
							onClick={() => reprocess_ticket(props.data.ocr_data.id,stage.Stage.promotion_id, (response) => {
								setIsProcessing(props.data.ocr_data.id);
							})}
							icon='knk-reload'
							autotranslate={false}
						>
							{(is_processing !== false) ? (
								<Reprocessing
									ticket_id={is_processing}
									promotion_id={stage.Stage.promotion_id}
									finished={(res) => {
										setIsProcessing(false);
	
										if(res?.stage_ocr_ticket) {
											props.updateStageTicket(res.stage_ocr_ticket)
										}
									}}
								/>
							) : i18n("Reprocess")}
						</ActionButton>
					)}

					{([1,3].includes(parseInt(props?.data?.ocr_data?.status ?? null)) || !is_ocr_advanced) && (
						<ActionButton
							onClick={() => {
								if(test_mode) return;
								resetPreviousSelection();
								setStatus('ok');
								if(props.onStatusChange != undefined) props.onStatusChange('ok');
							}}
							disabled={(test_mode) ? true : false}
							tooltip={(test_mode) ? <Tooltip
								with_icon={false}
								position='top'
							>The functionality to accept receipts is disabled in TEST MODE</Tooltip> : null}
							color='green'
							icon='knk-on'
						>Accept</ActionButton>
					)}
				</ButtonCollection>
			)}
		</>
	)
};

const ImageTicketWrapper = ({ in_layer, ...props }) => {
	const classes = ['image'];
	if(props?.classes ?? false) classes.push(props.classes);

	if(in_layer) {
		return (
			<div class={classes.join(" ")}>
				<PremoderationStatus
					status={props.status}
					created={props.created}
					display_pending={props.display_pending}
					custom_status={props.custom_status}
					intl={props.intl}
					promotion={props.promotion}
				/>

				<div class="scroller img_c" style={{ maxHeight:'var(--layer-max-h)' }}>
					{props.children}
				</div>
			</div>
		)
	}

	return (
		<div class={classes.join(" ")}>
			{props.children}
		</div>
	)
};

const ImageTicket = ({ image_url, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const in_layer = props?.in_layer ?? false;
	const wrapper_props = {
		in_layer,
		status:props.status,
		created:props.created,
		display_pending:props.display_pending,
		custom_status:props.custom_status,
		classes:props.classes,
		intl:props.intl,
		promotion:props.promotion,
	};

	if(image_url == undefined || image_url == '') {
		return (
			<ImageTicketWrapper {...wrapper_props}>
				<span class="empty_img">{i18n("No image")}</span>
			</ImageTicketWrapper>
		)
	}
	//let has_original = true;
	//if(image_url.indexOf('.webp') > -1) has_original = false;
	const original_prefix = props?.is_ocr_advanced ? '' : 'original_';

	return (
		<ImageTicketWrapper {...wrapper_props}>
			<img
				src={`${props.img_basedir}${image_url}`}
				alt=""
			/>

			{(in_layer) && (
				<ButtonCollection
					_class={{ other:'gui_flex_col zoom_in' }}
				>
					<SquaredButton
						icon='knk-magnify-in whiteButton'
						_href={`${props.img_basedir}${original_prefix}${image_url}`}
						_target='_blank'
						tooltip={<Tooltip
							with_icon={false}
							position='left'
						>View full-sized image</Tooltip>}
					/>

					{(props.toggleRawData != undefined) && (
						<SquaredButton
							icon='knk-embed whiteButton'
							onClick={props.toggleRawData}
							tooltip={<Tooltip
								with_icon={false}
								position='left'
							>Toggle RAW data</Tooltip>}
						/>
					)}
				</ButtonCollection>
			)}
		</ImageTicketWrapper>
	)
};

const PremoderationStatus = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const status = props?.status ?? '';
	const created = props?.created ?? false;
	const display_pending = props?.display_pending ?? false;
	const custom_status = (isNotEmpty(props, 'custom_status')) ? props.custom_status : false;

	const promotion = props.promotion;
	const tz = promotion.timezone;

	const generic_props = {
		_class:{
			other:'status_indicator',
		},
	};

	if(['ok', '1', 1].includes(status)) {
		return (
			<ButtonCollection
				{...generic_props}
			>
				<ActionButton
					color='green'
					icon='ep-thumbs-up-empty'
				>***Accepted</ActionButton>

				{(created) && (
					<span class="date">{EPGetTimezone(created, tz, datetime_format)}</span>
				)}
			</ButtonCollection>
		)
	}

	if(['ko', '3', 3].includes(status)) {
		return (
			<ButtonCollection
				{...generic_props}
			>
				<ActionButton
					color='red'
					icon='ep-thumbs-down-empty'
				>Rejected</ActionButton>

				{(created) && (
					<span class="date">{EPGetTimezone(created, tz, datetime_format)}</span>
				)}
			</ButtonCollection>
		)
	}

	if(custom_status) {
		return (
			<ButtonCollection
				{...generic_props}
			>
				<ActionButton
					color='white'
					autotranslate={false}
				>{i18n(custom_status)}</ActionButton>
			</ButtonCollection>
		)
	}

	if(display_pending) {
		return (
			<ButtonCollection
				{...generic_props}
			>
				<ActionButton
					color='orange'
					icon='knk-loading'
					autotranslate={false}
				>{i18n("In review")}</ActionButton>
			</ButtonCollection>
		)
	}

	return (
		<span />
	);
};

export const RequirementsOCRSimple = ({ data, promotion, stage, ...props }) => {
	useCSS('comp/premoderation.css');

	const [ view_raw_data, setViewRawData ] = useState(false);
	const [ stage_ticket, setStageTicket] = useState(false);

	const tz = promotion.timezone;

	useEffect(() => {
		fetch(getRemotedbUrl(`/v24/ocr/ticket/${promotion.id}/${data.ocr_data}`)).then((r) => r.json()).then((r) => {
			if(r.status) {
				setStageTicket(r.ticket);
			}

			if(r.status == 0) {
				props.close();
			}
		})
	}, []);

	if(stage_ticket === false) return null;
	const is_ocr_advanced = ((data?.ocr_ticket_id ?? data?.data?.ocr_ticket_id) != null) ? true : false;
	return (
		<FloatingLayer
			backButton={props.close}
			_class={{
				size:'full',
			}}
		>
			<div class="without_actions size_controller">
				<ModuleHeader>Receipt details</ModuleHeader>

				<div class="ticket_full_preview">
					<ImageTicket
						is_ocr_advanced={is_ocr_advanced}
						image_url={is_ocr_advanced ? stage_ticket.StageOcrTicket.image : 'original_' + stage_ticket.StageOcrTicket.image}
						img_basedir={props.img_basedir}
						display_pending={false}
						in_layer={true}
						intl={props.intl}
						promotion={promotion}
						toggleRawData={is_ocr_advanced ? () => setViewRawData(!view_raw_data) : null}
					/>

					{(view_raw_data !== false) && (
						<TicketRawDataVisualization
							data={{ ...data, ocr_data:stage_ticket.StageOcrTicket }}
							backButton={() => setViewRawData(false)}
						/>
					)}

					{(view_raw_data === false) && (
						<div class="c">
							<UserFullInfo
								data={data}
								fields={stage.Stage.reqs_form}
								promotion_id={stage.Stage.promotion_id}
								openWholeUserLayer={data.openDetail}
								close={props.close}
								intl={props.intl}
								promotion={promotion}
							/>

							<OcrDataPremoderation
								ocr_data={stage_ticket.StageOcrTicket}
								stage={stage.Stage}
								tz={tz}
								validation_tags={props?.validation_tags ?? []}
								display_validation={false}
								intl={props.intl}
							/>
						</div>
					)}
				</div>
			</div>
		</FloatingLayer>
	)
};

const Requirements = forwardRef(({ stage, accept, reject, openWholeUserLayer, ...props },ref) => {
	const { id } = useParams();
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? false;
	const items_per_page = 12;
	
	const [ display_detail, setDisplayDetail ] = useState(false);
	const [ next_ticket, setNextTicket ] = useState(false);
	const [ pre_status, setPreStatus ] = useState(null);
	const [ is_processing, setIsProcessing ] = useState(false);
	const [ status, setStatus ] = useState(null);
	const [ period, setPeriod ] = useState(null);
	const [ search_user, setSearchUser ] = useState(null);
	const [ is_comparing, setIsComparing ] = useState(false);
	const [ tickets_to_compare, setTicketsToCompare ] = useState(null);
	const [ view_raw_data, setViewRawData ] = useState(false);

	const getDateFromPeriod = (days) => {
		return EPDateSubtractAndFormat(null, 'days', days, 'YYYY-MM-DD HH:mm:ss');
	};

	const getReqs = () => {
		const stage_id = id != undefined ? id : stage.Stage.id;
		return $.getJSON(getRemotedbUrl(`/users/fetch_requirements_premoderate/${promotion.id}/${stage_id}`));
	};
	
	const getTicketsToCompare = (stage_id,stageTicket) => {
		setIsComparing(true);
		$.get(getRemotedbUrl(`/users/get_receipts_from_user/${promotion.id}/${stage_id}/${stageTicket.login_id}`), (res) => {
			const tickets = res.status ? (res.tickets).filter((item)=> item.StageTicket.id != stageTicket.id) : [];//No volem el seu propi pendent
			setTicketsToCompare(tickets);
		}, 'JSON');
	};
	
	const deleteTicket = (stage_id,stageTicket,cb) => {
		const base_url = `/users/requirements_premoderate/${promotion.id}`;
		$.post({
			url:getRemotedbUrl(`${base_url}/delete`),
			data:{
				_data:encodeBase64Fake({ action:'delete', stage_id, ticket_id:stageTicket.id }),
			},
			dataType:'JSON',
			success:(response) => {
				if(response.status){
					closeDetailLayer();
					dispatch({
						type:'REMOVE_GENERIC',
						Model:'StageTicket',
						id:stageTicket.id
					});
				}
				if(cb != undefined) cb();
			}
		}).fail((xhr, ajaxOptions, thrownError) => {
			throwErrorEvent(xhr.status);
		});
	};

	const { isLoading, isError, data, error } =  useQuery('requirements', () => getReqs(), { refetchOnWindowFocus:false });

	const [ state, dispatch ] = useFilterData(data, null, [{
		filter:{
			key:'status',
			name:"Status",
			type:'=',
			tier1:['StageTicket.status'],
		},
		key:'status',
		operator:'=',
		value:2,
	}, {
		filter:{
			key:'created',
			name:"Created",
			type:'date',
			tier1:['StageTicket.created'],
		},
		key:'created',
		operator:'>=',
		value:getDateFromPeriod(1),
	}]);

	useEffect(() => {
		if(state.filtered.length == 0) {
			// returned no tickets, so we increase the filter:
			filterByPeriod(30);
		}
	}, []);
	
	useEffect(() => {
		if(data == undefined) return;
	
		dispatch({ type:'SETDATA', data });
	}, [ data ]);


	const updateStageTicket = (stage_ocr_ticket) => {
		const bulk = (r) => {
			r.StageOcrTicket = stage_ocr_ticket.StageOcrTicket;
			return r;
		}

		dispatch({
			type:'APPLYBULK_GENERIC',
			bulk,
			Model:'StageOcrTicket',
			ids:[stage_ocr_ticket.StageOcrTicket.id],
		});

		setDisplayDetail((current) => {
			current.ocr_data = stage_ocr_ticket.StageOcrTicket;
			return { ...current };
		});
	};

	useImperativeHandle(ref, (data, state) => ({
		updateRequirement(type, user) {
			switch (type) {
				case 'edit_user':
					updateUser(user);
					break;

				default:
					break;
			}
		}
	}));

	const filterByStatus = (value) => {
		const filter = {
			key:'status',
			name:"Status",
			type:'=',
			tier1:['StageTicket.status'],
		};
		
		if(value) {
			dispatch({
				type:'SETFILTER',
				filter:{
					key:'status',
					value,
					operator:'=',
					filter,
				},
				unique:true,
			});
		} else {
			dispatch({ type:'RESET_FILTERS' });
		}

		setStatus(value);
	};

	const searchUser = (value) => {
		const filter = {
			key:'search_user',
			name:"Name",
			tier1:['StageTicket.user_data.name'],
			tier2:{ ...operators.textOperators },
			tier3:'input',
		};

		dispatch({
			type:'SETFILTER',
			filter:{
				key:'search_user',
				value,
				operator:'like_all',
				filter,
			},
			unique:true,
		});

		setSearchUser(value);
	};

	const filterByPeriod = (period) => {
		const filter = {
			key:'created',
			name:"Created",
			type:'date',
			tier1:['StageTicket.created'],
		};

		dispatch({
			type:'SETFILTER',
			filter:{
				key:'created',
				value:getDateFromPeriod(period),
				operator:'>=',
				filter,
			},
			unique:true,
		});

		setPeriod(period);
	};

	useEffect(() => {
		if(props.section == undefined) return;

		switch(props.section) {
			case 'rejected':
				filterByStatus([3,9]);
				break;
			case 'activity':
				filterByStatus(null);
				break;
			case 'inreview':
				filterByStatus(2);
				break;
		}
	}, [ props.section ]);

	const [ requirements, pagination, goToPage ] = usePaginator(state.filtered, items_per_page);

	const displayZoomedTicket = (id) => {
		const ticket = (state?.filtered ?? []).find((r) => (r.StageTicket.id == id));
		const actual_ticket = ticket?.StageTicket ?? null;
		actual_ticket.ocr_data = ticket?.StageOcrTicket ?? null;
		setDisplayDetail(actual_ticket);
		setNextTicket(goNextTicket(actual_ticket));
	};

	const goNextTicket = (actual_ticket) => {
		const index = (state.filtered).findIndex((item) => item.StageTicket.id === actual_ticket.id);
		if((state.filtered).length == index + 1) return false;	// no more tickets in da house
		return (state.filtered)[index + 1].StageTicket.id;
	};

	const closeDetailLayer = () => {
		setDisplayDetail(false);
		setIsProcessing(false);
		setTicketsToCompare(null);
		setIsComparing(false)
		setPreStatus(null);
	};

	const changeStatus = (id, status, reason) => {
		if(reason == undefined)reason=null;
		const bulk = (r) => {

			let new_data = {...r.StageTicket.data, agent_id:props.agent_id};
			if(reason) new_data = {...new_data, reason, rejected:EPGetDateValid()};
			else new_data = {...new_data, reason, accepted:EPGetDateValid()};

			r.StageTicket.data = new_data;
			r.StageTicket.status = status;
			return r;
		}
		dispatch({
			type:'APPLYBULK_GENERIC',
			bulk,
			Model:'StageTicket',
			ids:[id],
		});
	};

	const incrementTicketsTotal = (login_id) => {
		const tickets_ids_from_login = (data?.data ?? []).filter((item)=> item.StageTicket.login_id == login_id).map(obj => obj.StageTicket.id);
		const bulk = (r) => {
			r.StageTicket.total++;
			return r;
		}
		dispatch({
			type:'APPLYBULK_GENERIC',
			bulk,
			Model:'StageTicket',
			ids:tickets_ids_from_login,
		});
	}

	const updateUser = (user) => {
		const tickets_ids_from_login = (data?.data ?? []).filter((item)=> item.StageTicket.login_id == user.login_id).map(obj => obj.StageTicket.id);
		const bulk = (r) => {
			r.StageTicket.user_data = {...r.StageTicket.user_data,
				name : user?.nickname ?? user.name,
				email : user?.email ?? '',
				first_name : user?.first_name ?? '',
				last_name : user?.last_name ?? ''
			}
			return r;
		}
		dispatch({
			type:'APPLYBULK_GENERIC',
			bulk,
			Model:'StageTicket',
			ids:tickets_ids_from_login,
		});
	};

	const onAccept = (participations) => {
		// if (display_detail?.ocr_data) {
		// 	closeDetailLayer();
		// 	return;
		// }
		setIsProcessing(true);
		accept(stage, display_detail.id, participations, () => {
			changeStatus(display_detail.id, 1);
			incrementTicketsTotal(display_detail.login_id);
			if(next_ticket !== false) {
				setIsProcessing(false);
				setPreStatus(null);
				displayZoomedTicket(next_ticket);
			} else {
				closeDetailLayer();
			}
		});
	};

	const onReject = (reason) => {
		setIsProcessing(true);
		reject(stage, display_detail.id, reason, () => {
			changeStatus(display_detail.id, 3, reason);
			incrementTicketsTotal(display_detail.login_id);
			if(next_ticket !== false) {
				setIsProcessing(false);
				setPreStatus(null);
				displayZoomedTicket(next_ticket);
			} else {
				closeDetailLayer();
			}
		});
	};
	const section = props?.section ?? 'inreview';
	const wrapper_props = {
		status,
		period,
		search_user,
		filterByStatus,
		filterByPeriod,
		searchUser,
		intl:props.intl,
		section,
		stage,
		totals:{},
		is_premoderator:props.is_premoderator,
		go_back_section:props.go_back_section
	};

	const tickets = state?.data ?? [];

	if(tickets.length > 0) {
		wrapper_props.totals.pending = tickets.filter((i) => (parseInt(i.StageTicket.status) == 2)).length;
		wrapper_props.totals.rejected = tickets.filter((i) => (parseInt(i.StageTicket.status) == 3) || parseInt(i.StageTicket.status) == 9).length;
	}

	if(isLoading) {
		return (
			<Wrapper {...wrapper_props}>
				<Spinner>Loading requirements</Spinner>
			</Wrapper>
		)
	}
	if(state.filtered == undefined || state.filtered.length == 0) {
		return (
			<Wrapper {...wrapper_props}>
				<div class="is_fullscreen relative_to_container gui_fully_centered gui_flex_col">
					<p>{i18n("There are no pending receipts.")}</p>

					<p><strong>{i18n("You can try to change the filters.")}</strong></p>
				</div>
			</Wrapper>
		)
	}

	const status_ticket = parseInt(display_detail?.status ?? 2);
	const status_accepted = [0,1].includes(status_ticket);
	const status_rejected = [3].includes(status_ticket);
	const status_error = [9].includes(status_ticket);
	const status_validated = status_accepted || status_rejected || status_error;

	//const direct_stage = (stage.Stage.type == stage_categories.ID_FORM_CATEGORY.id) ? true : false;
	//const punch_card_stage = (stage.Stage.type == stage_categories.ID_STAMPSCARD_CATEGORY.id) ? true : false;

	// The OK can be sent through the emails platform:
	//const is_automatic_participation = ((direct_stage && !stage.Stage.reqs_premoderation_manual_participation) || punch_card_stage) ? true : false;
	
	const disabled_review = !isStageOpen(stage.Stage,promotion);
	const test_mode = promotion?.test ?? false;
	const tz = promotion.timezone;

	const is_ocr_advanced = (stage.Stage.reqs_type == 8) ? true : false;

	return (
		<Wrapper {...wrapper_props}>
			<Router basename={`${props.basename}`}>
				<div>
					<Route path="*" render={() => {
						if(['inreview', 'rejected'].includes(section)) {
							return (
								<>	
									{(disabled_review) && (
										<AlertMessage _class={{other:'in_top'}} icon="ep-exclamation redIcon">
											<p>{i18n("Participation in the promotion has ended. To review the pending purchase receipts, it is necessary to reopen the participation date.")}</p>
										</AlertMessage>
									)}

									<ul class="ticket_gallery">
										{(state.filtered).map((_req) => { //NO hi ha paginacio => state.filtered
											const ticket = _req?.StageTicket ?? {};
											const data = ticket?.data ?? {};
											const user_name = ticket?.user_data?.name ?? false;

											const _status = parseInt(ticket?.status ?? '2');	// status '2' means "pending to be moderated"

											const is_accepted = ([0, 1].includes(_status)) ? true : false;
											const is_rejected = ([3].includes(_status)) ? true : false;
											const is_error = ([9].includes(_status)) ? true : false;

											const is_validated = is_accepted || is_rejected || is_error;

											const classes = ['single_ticket'];
											if(disabled_review) classes.push('disabledItem');
											if(is_validated) classes.push('is_validated', `is_${(is_rejected || is_error) ? 'ko' : 'ok'}`);

											const display_review = !is_validated && !disabled_review;
											if(is_rejected || is_error)classes.push('cursor_pointer')

											return (
												<li
													key={`log_${ticket.id}`}
													class={classes.join(" ")}
													onClick={() => !disabled_review ? displayZoomedTicket(ticket.id) : null}
												>
													<ImageTicket
														is_ocr_advanced={is_ocr_advanced}
														image_url={data.graphic_info}
														img_basedir={props.img_basedir}
														in_layer={false}
														intl={props.intl}
														promotion={promotion}
													/>

													{(user_name !== false) && (
														<p class="name">
															<strong class="gui_cutTextOnOverflow">{user_name}</strong>
														</p>
													)}

													<p class="date">{`${EPGetTimezone(ticket.created, tz, datetime_format)}`}</p>

													{(display_review) && (
														<ActionButton
															_class={{ other:'not_animated' }}
														>Review</ActionButton>
													)}

													{(is_rejected || is_error) && (
														<ActionButton
															icon='ep-thumbs-down-empty'
															color='red'
															_class={{other:'gui_remove_pointer_events'}}
														>Rejected</ActionButton>
													)}

													{(is_accepted) && (
														<ActionButton
															icon='ep-thumbs-up-empty'
															color='green'
														>***Accepted</ActionButton>
													)}

													{(is_error) ? (
														<>{`${i18n("By system")}`}</>
													): (is_rejected) && (
														<>{`${i18n("By agent")}`}</>
													)}

													{(is_rejected && false) && (
														<SquaredButton
															icon='knk-visible'
															_class={{ other:'view_detail' }}
															//style={{ top: '30%',left: '50%', transform: 'translate(-30%, -50%)'}}
														/>
													)}
												</li>
											)
										})}
									</ul>
								</>
							)
						}
						
						if(['activity'].includes(section)) {
							return (
								<>
									<TableOfContents
										items={requirements.filter((item) => [0, 1, 2, 3].includes(parseInt(item.StageTicket.status)))}
										title={`${i18n("Requirements")}`}
										empty_message={i18n("There are no results.")}
										created={(item) => {
											const ticket = item?.StageTicket ?? {};
											return (
												<div>
													{EPGetTimezone(ticket.created, tz, datetime_format)}
												</div>
											)
										}}
										agent={(item) => {
											const ticket = item?.StageTicket ?? {};
											const ticket_detail = ticket?.data ?? {};
											return (
												<div>
													{(ticket_detail.agent_id == props.agent_id || !ticket_detail.agent_id) ? (
														`${i18n("You")}`
													) : (
														`${ticket?.agent_name}`
													)}
												</div>
											)
										}}
										user={(item) => {
											const ticket = item?.StageTicket ?? {};
											const user = ticket?.user_data ?? false;
											const login_id = user?.login_id ?? null;
											return (
												<div>
													{(login_id) ? (
														<strong><ActionLink
															onClick={() => {
																openWholeUserLayer(login_id);
															}}
															autotranslate={false}
														>{user.name}</ActionLink></strong>
													): (
														<strong>{user.name}</strong>
													)}
												</div>
											)
										}}
										action={(item) => {
											const ticket = item?.StageTicket ?? {};
											const ticket_detail = ticket?.data ?? {};
											const ticket_status = parseInt(ticket?.status ?? 2);

											let date_accepted,date_rejected,date_error,not_validated = null;
											if(!is_ocr_advanced){
												date_accepted = ([0, 1].includes(ticket_status)) && isNotEmpty(ticket_detail, 'accepted') ? ticket_detail.accepted : null;
												date_rejected = ([3].includes(ticket_status)) && isNotEmpty(ticket_detail, 'rejected') ? ticket_detail.rejected : null;
												not_validated = [1].includes(ticket_status) && !date_accepted && !date_rejected;//"Accepted" before enable premoderation tool
											}else{
												date_accepted = ([0, 1].includes(ticket_status)) && isNotEmpty(ticket_detail, 'ocr_ticket_id') ? ticket.created : null;
												date_rejected = ([3].includes(ticket_status)) && isNotEmpty(ticket_detail, 'ocr_ticket_id') ? ticket.created : null;
												date_error = ([9].includes(ticket_status)) && isNotEmpty(ticket_detail, 'ocr_ticket_id') ? ticket.created : null;
												not_validated = [1].includes(ticket_status) && !date_accepted && !date_rejected && !date_error;
											}
											const in_review = [2].includes(ticket_status);

											let label_status = i18n("No review");
											let label_tooltip = null;
											let ribbon_color = 'yellow';

											if(in_review) {
												label_status = i18n("In review");
												label_tooltip = `${i18n("Under review")}`;
												ribbon_color = 'gray';
											} else if(!not_validated) {
												label_status = date_accepted ? i18n("***Accepted") : i18n("Rejected");
												label_tooltip = date_accepted ? `${i18n("Accepted on")} ${EPGetTimezone(date_accepted, tz, datetime_format)}` : `${i18n("Rejected on")} ${EPGetTimezone(date_rejected, tz, datetime_format)}`;
												ribbon_color = date_accepted ? 'green' : 'red';
											}
	
											return (
												<div>
													<RibbonCollection
														items={[{
															title:label_status,
															color:ribbon_color,
															tooltip:<Tooltip
																with_icon={false}
																position='top'
															>{label_tooltip}</Tooltip>
														}]}
													/>
												</div>
											)
										}}
										actions={(item) => {
											const ticket = item?.StageTicket ?? {};
											const ticket_detail = ticket?.data ?? {};
											const ticket_status = parseInt(ticket?.status ?? 2);
											const in_review = [2].includes(ticket_status);
											
											return (
												<ButtonCollection _class={{ other:'gui_flex_end' }}>
													<SquaredButton
														icon={in_review ? 'knk-loading' : 'knk-visible'}
														onClick={() => !in_review ? displayZoomedTicket(ticket.id) : null}
														_class={{other:in_review ? 'gui_withoutEvents grayIcon' : ''}}
														tooltip={in_review ? null : <Tooltip
															with_icon={false}
															position='top'
														>View details</Tooltip>}
													/>
												</ButtonCollection>
											)
										}}
										intl={props.intl}
									/>
		
								{(pagination.total_pages > 1) && (
									<Paginator
										{...pagination}
										goToPage={goToPage}
										intl={props.intl}
										min_pages_to_display={2}
									/>
								)}
							</>
							)
						}
					}}/>
				</div>
			</Router>

			{(display_detail !== false) && (
				<FloatingLayer
					backButton={closeDetailLayer}
					_class={(is_ocr_advanced || is_comparing || true) ? {
						size:'full',
					} : null}
				>
					<div class="without_actions size_controller">
						<ModuleHeader
							actions={(is_comparing) ? <>
								<SecondaryButton
									icon='knk-arrow-l-big'
									onClick={() => setIsComparing(false)}
									_class={{ other:'not_in_end' }}
								>Review current receipt</SecondaryButton>

								<ActionButton
									color='green'
									icon='knk-on'
									disabled={(test_mode) ? true : false}
									tooltip={(test_mode) ? <Tooltip
										with_icon={false}
										position='bottom'
									>The functionality to accept receipts is disabled in TEST MODE</Tooltip> : null}
									onClick={() => {
										if(test_mode) return;

										setIsComparing(false);
										setPreStatus('ok');
									}}
								>Accept</ActionButton>

								<ActionButton
									color='orange'
									icon='knk-help'
									onClick={closeDetailLayer}
								>Decide later</ActionButton>

								<ActionButton
									color='red'
									icon='knk-off'
									onClick={() => {
										setIsComparing(false);
										setPreStatus('ko');
									}}
								>Reject</ActionButton>
							</> : <>
								{(!status_validated && (display_detail?.total ?? 0) > 0) && (
									<FormattedMessage
										id="This user has also {num} receipts ({link})."
										values={{
											num:display_detail.total,
											link:<ActionLink
												onClick={() => getTicketsToCompare(stage.Stage.id, display_detail)}
											>Compare</ActionLink>,
										}}
									/>
								)}

								{(status_rejected || status_error) && (
									<SaveButton
										type="confirm"
										color='red'
										icon='knk-delete'
										onClick={(e,cb) => deleteTicket(stage.Stage.id,display_detail,cb)}
										square={true}
									/>
								)}

								{(status_rejected || status_error) && (
									<ActionButton
										color='red'
										icon='ep-thumbs-down-empty'
										_class={{other:'gui_remove_pointer_events'}}
									>Rejected</ActionButton>
								)}

								{(status_accepted) && (
									<ActionButton
										color='green'
										icon='ep-thumbs-up-empty'
										_class={{other:'gui_remove_pointer_events'}}
									>***Accepted</ActionButton>
								)}
							</>}
						>Receipt details</ModuleHeader>

						<div class="ticket_full_preview">
							{(!is_processing) && (
								<>
									<ImageTicket
										is_ocr_advanced={is_ocr_advanced}
										image_url={display_detail.data.graphic_info}
										img_basedir={props.img_basedir}
										status={pre_status}
										display_pending={(is_comparing) ? true : false}
										in_layer={true}
										classes={(is_comparing) ? 'highlight' : null}
										custom_status={(is_comparing) ? "Current" : null}
										intl={props.intl}
										promotion={promotion}
										toggleRawData={is_ocr_advanced ? () => setViewRawData(!view_raw_data) : null}
									/>

									{(!is_comparing && view_raw_data) && (
										<TicketRawDataVisualization
											data={display_detail}
											backButton={() => setViewRawData(false)}
										/>
									)}

									{(!is_comparing && view_raw_data === false) && (
										<TicketValidation
											key={`options_${display_detail.id}`}
											stage={stage}
											data={display_detail}
											status={pre_status}
											onAccept={onAccept}
											onReject={onReject}
											agent_id={props.agent_id}
											openWholeUserLayer={openWholeUserLayer}
											close={closeDetailLayer}
											onStatusChange={setPreStatus}
											validation_tags={props.validation_tags}
											is_last_ticket={(next_ticket === false) ? true : false}
											intl={props.intl}
											test_mode={test_mode}
											promotion={promotion}
											updateStageTicket={updateStageTicket}
										/>
									)}

									{(is_comparing) && (
										<UserTicketComparison
											stage={stage}
											tickets_to_compare={tickets_to_compare}
											img_basedir={props.img_basedir}
											intl={props.intl}
											promotion={promotion}
										/>
									)}
								</>
							)}

							{(is_processing) && (
								<Spinner />
							)}
						</div>
					</div>
				</FloatingLayer>
			)}
		</Wrapper>
	)
});

const PromotionRequirementsTool = (props) => {
	useCSS('comp/premoderation.css');

	const base_url = `/users/requirements_premoderate/${props.promotion.id}`;
	const basename = `${base_url}`;//'/';
	const img_basedir = imgPath(props.bucket, props.promotion.id, 'entries/customfields/large');

	const [ segments, setSegments ] = useState(props?.segments ?? []);
	const [ display_full_entry, setDisplayFullEntry ] = useState(false);

	const { isLoading,data:validation_tags } =  useQuery('validation_tags', ()=>get_all_validators(props.promotion.account_id), { refetchOnWindowFocus:false });

	const openWholeUserLayer = (login_id) => {
		$.get(getRemotedbUrl(`/users/get_full_view_data/${props.promotion.id}/${login_id}`), (res) => {
			if(res.status == '1') {
				setDisplayFullEntry(res.data);
			}
		}, 'JSON').fail((xhr, ajaxOptions, thrownError) => {
			throwErrorEvent(xhr.status);
		});
	};

	const closeWholeUserLayer = () => {
		setDisplayFullEntry(false);
	};
	
	const segmentCreated = (segment)=>{
		setSegments([...segments,segment]);
	}

	const segmentDeleted = (user_id,segment_id) => {
		const _user = display_full_entry;
			_user.user_data.segments = _user.user_data.segments.filter(s=>s!=segment_id);
		setDisplayFullEntry(_user);
	}

	const accept = (stage, ticket_id, participations, cb) => {
		$.post({
			url:getRemotedbUrl(`${base_url}/accept`),
			data:{
				_data:encodeBase64Fake({ action:'accept', stage_id:stage.Stage.id, ticket_id, participations }),
			},
			dataType:'JSON',
			success:(response) => {
				if(cb != undefined)cb();
			},
		}).fail((xhr, ajaxOptions, thrownError) => {
			throwErrorEvent(xhr.status);
		});
	};

	const reject = (stage, ticket_id, reason, cb) => {
		$.post({
			url:getRemotedbUrl(`${base_url}/reject`),
			data:{
				_data:encodeBase64Fake({ action:'reject', stage_id:stage.Stage.id, ticket_id, reason }),
			},
			dataType:'JSON',
			success:(response) => {
				if(cb != undefined) cb();
			}
		}).fail((xhr, ajaxOptions, thrownError) => {
			throwErrorEvent(xhr.status);
		});
	};

	
	const reqs = useRef();
	const is_premoderator = props.premoderator ? true : false;

	const sections_root = {
		label:"Go back",
		full_link: getRemotedbUrl(is_premoderator ? `/promotions` : `/promotions/profile/${props.promotion.id}`),
		icon:'knk-arrow-l-big',
	};

	const sections_stage = {
		label:"Go back",
		full_link:'/',
		icon:'knk-arrow-l-big',
		router:true,
	};

	return (
		<Router basename={`${basename}`}>
			<div>
				<Route exact={true} path={'/'} render={(route) => {

					if(props.stages.length == 1) {
						return (
							<Redirect to={`/${props.stages[0].Stage.id}`} />
						)
					}

					return (
						<section class="gui_users_registered">
							<StageSelector
								stages={props.stages}
								back = {getRemotedbUrl(is_premoderator ? `/promotions` : `/promotions/profile/${props.promotion.id}`)}
								url='/'
							/>
						</section>
					)
				}} />

				<Route exact={true} path={`/:id/:section?`} render={(route) => {
					const stage = props.stages.filter((obj) => { return obj.Stage.id == route.match.params.id})[0];
					if(stage == undefined) return null;

					return (
						<Requirements
							ref={reqs}
							stage={stage}
							promotion={props.promotion}
							img_basedir={img_basedir}
							intl={props.intl}
							reject={reject}
							accept={accept}
							validation_tags={validation_tags}
							agent_id={props.agent_id}
							is_premoderator={is_premoderator}
							go_back_section={(props.stages.length > 1) ? sections_stage : sections_root}
							openWholeUserLayer={(id) => openWholeUserLayer(id)}
							basename={`${basename}/${stage.Stage.id}`}
							section={route?.match?.params?.section ?? 'inreview'}
						/>
					)
				}} />
			</div>
			
			{(display_full_entry !== false) && (
				<FloatingLayer backButton={() => closeWholeUserLayer()}>
					<div class="without_actions">
						<SingleRegisteredEntry
							{...display_full_entry}
							segments={segments}
							segmentDeleted={segmentDeleted}
							segmentCreated={segmentCreated}
							userAction={(type, c, b, d) => SecurityUserAction(type, c, b, (response) => {
								closeWholeUserLayer();
								reqs.current.updateRequirement(type,response)
							},props.promotion.id)}
							useOwner={useOwner()}
							skip_manual_prize={true}
							skip_actions={is_premoderator}
							validation_tags={validation_tags}	// only for OCR Ticket
							img_basedir={img_basedir}			// only for OCR Ticket
							//basename={`${basename}${stage.Stage.id}`}
							//setBulkModalAction={setBulkModalAndAction}
						/>
					</div>
				</FloatingLayer>
			)}
		</Router>
	);
}

export default injectIntl(PromotionRequirementsTool);